import { BAKED_RELEASE } from './manifest';

/**
 * The one interruption left in the product.
 *
 * ## What this is not, any more
 *
 * This module used to ration an update BAR — a number cut once a night so the
 * "New version available" prompt appeared daily rather than hourly. The bar is
 * gone (see `src/lib/update-apply.ts` for why asking at all was the mistake),
 * and the nightly cut went with it. Nothing schedules this number now.
 *
 * ## What it is
 *
 * `public/release.json` carries an integer that somebody bumps BY HAND, and
 * only for a fix people are actively being hurt by — a bug losing work, a
 * broken send, an outage. A client whose baked number is behind the served one
 * applies its pending update at the next safe instant instead of waiting for a
 * navigation or a reopen.
 *
 *   1. Edit `public/release.json`, increment `release`.
 *   2. Merge. It deploys like anything else.
 *
 * 🚨 It is not a version, a changelog or a cadence, and nothing displays it. It
 * answers exactly one question: *is this bad enough to reload somebody mid-job
 * over?* The honest answer is almost always no — the ordinary path already gets
 * a fix to nearly everybody within the hour without them noticing — so a bump
 * should feel like a decision, and be rare enough to be one.
 *
 * 🚨 A forced update still cannot interrupt a live voice conversation. See
 * `shouldApplyOnNavigation`: a reload hold outranks this, because hanging up on
 * a customer mid-sentence is worse than ten more minutes of the bug.
 */
export function shouldForceApply(facts: {
  /** The number baked into the code currently running. */
  baked: number;
  /** The number the server is serving now, or null if we could not find out. */
  live: number | null;
}): boolean {
  // 🚨 No answer is NOT a reason to reload. A missing or malformed release.json
  // fails closed — quiet — because the alternative fails towards interrupting
  // people for no reason at all, and because a client that is never forced
  // still updates on its own at the next navigation or reopen. If this goes
  // wrong the symptom is a forced fix taking an hour instead of a minute, not a
  // customer being reloaded out of a certificate.
  if (facts.live === null) return false;
  return facts.live > facts.baked;
}

export interface LiveRelease {
  release: number;
}

/**
 * What is the server serving right now?
 *
 * `cache: 'no-store'` and the service worker's own bypass (see the
 * `RELEASE_PATH` guard in `public/sw.js`) both matter: a marker answered from a
 * cache is a marker that can never move, which reads exactly like "nothing is
 * urgent" for as long as the entry lives — on the one file whose entire job is
 * to say that something is.
 */
export async function fetchLiveRelease(): Promise<LiveRelease | null> {
  try {
    const res = await fetch('/release.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    if (typeof body !== 'object' || body === null) return null;
    const { release } = body as { release?: unknown };
    if (typeof release !== 'number' || !Number.isFinite(release)) return null;
    return { release };
  } catch {
    // Offline, or the file is not there. Quiet, per the note above.
    return null;
  }
}

export { BAKED_RELEASE };
